import React, { useEffect, useState } from 'react'

import i18next from '@disco/i18n/i18next'
import { loadBusinessSubscription } from '@disco/state-management/actions/UserActions'
import { getSubscriptionPeriod } from '@disco/state-management/selectors/subscription'
import {
  getConstanceSettings,
  getDSQuota,
  getDSQuotaUsed,
  getUser,
} from '@disco/state-management/selectors/user'
import captureError from '@disco/utils/captureError'
import { getWatermarkingQuota } from '@disco/utils/getWatermarkingQuota'
import { formatDate } from '@disco/utils/time-utils'

import ChargebeeScriptLoader from 'components/chargebee-form/chargebee-loader'
import DiscoverySuiteAddOns from 'components/common/subscribed-user-billing/add-ons/DiscoverySuiteContainer'
import WatermarkingAddOns from 'components/common/subscribed-user-billing/add-ons/WatermarkingContainer'
import { SubscribedUserBillingCancelButton } from 'components/common/subscribed-user-billing/SubscribedUserBillingCancelButton'
import { Callout, Flex, Spinner } from 'components/ds'
import ContactUsLink from 'components/ui/contact-us-link'

import useChargebeePricingPage from 'hooks/use-chargebee-pricing-page'
import { useDispatch, useSelector } from 'hooks/use-redux'

import YourSubscription from './index'
import {
  formatBillingAmount,
  getActiveAddOns,
  getAddOnsTotal,
  getBilledToLines,
  getCardDetails,
  hasAddOnCharge,
} from './utils'

/**
 * Loads the business's Chargebee subscription and renders the "Your subscription" section,
 * wiring Manage subscription to the Chargebee pricing page and Cancel to the existing flow.
 */
const YourSubscriptionContainer = (): React.ReactNode => {
  const dispatch = useDispatch()
  // TODO: STELLA-31 - translate the section's copy, with its ja and ko strings, in a follow-up PR
  const locale = i18next.language
  const user = useSelector(getUser)
  const constance = useSelector(getConstanceSettings)
  const subscription = useSelector(state => state.subscription)
  const period = useSelector(getSubscriptionPeriod)
  const discoverySuiteQuota = useSelector(getDSQuota)
  const discoverySuiteUsed = useSelector(getDSQuotaUsed)
  // Selected field by field: getWatermarkingQuota builds a new object each call, which would re-render on every dispatch
  const watermarkingQuota = useSelector(
    state => getWatermarkingQuota(state).quota ?? 0,
  )
  const watermarkingUsed = useSelector(
    state => getWatermarkingQuota(state).quotaUsed,
  )
  const watermarkingUnlimited = useSelector(state =>
    Boolean(getWatermarkingQuota(state).quotaUnlimited),
  )

  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
    useState(false)
  const [hasChargebeeScriptLoaded, setHasChargebeeScriptLoaded] =
    useState(false)

  const isAdmin = Boolean(
    user.permissions?.includes('can_change_business_settings'),
  )
  const pricingPage = useChargebeePricingPage('/settings/billing', isAdmin)
  const isBankDraftSubscriber =
    user.business?.private_metadata?.plan_type === 'bank_draft_subscriber'
  const chargebee = subscription.chargebee

  useEffect(() => {
    if (
      !subscription.loading &&
      !subscription.loaded &&
      !isBankDraftSubscriber
    ) {
      dispatch(loadBusinessSubscription())
    }
    // Mount only: a failed load leaves loaded false, so re-running on change would retry forever
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (subscription.loading && !chargebee) return <Spinner />

  if (subscription.error?.body?.error === 'invalid_subscription_id') {
    return (
      <Callout.Root color="red">
        <Callout.Text>
          Sorry, we couldn’t find a subscription for this account.{' '}
          <ContactUsLink intercomMsg="My billing page is showing 'subscription not found'.">
            Please contact support.
          </ContactUsLink>
        </Callout.Text>
      </Callout.Root>
    )
  }

  const needsChargebeeScript =
    constance.SELF_SERVE_CANCELLATION_ROLLOUT &&
    constance['SUB-735-CHARGEBEE-CANCELLATION']
  const addOnCharges = chargebee?.addons ?? []
  const hasDiscoverySuite = hasAddOnCharge(addOnCharges, 'discovery_suite')
  const hasWatermarking = hasAddOnCharge(addOnCharges, 'watermarking')
  const activeAddOns = getActiveAddOns({
    charges: addOnCharges,
    currencyCode: chargebee?.currency_code,
    locale,
    discoverySuiteQuota,
    discoverySuiteUsed,
    watermarkingQuota,
    watermarkingUsed,
    watermarkingUnlimited,
  })
  const addOnsTotal = getAddOnsTotal({
    charges: addOnCharges,
    period: period ?? null,
    currencyCode: chargebee?.currency_code,
    locale,
  })
  // Without the subscription we can't tell which add-ons are owned, as on the legacy page
  const hasAddOnOffers =
    !subscription.error && (!hasDiscoverySuite || !hasWatermarking)

  // As on the legacy page, so admins can cancel even when the subscription fails or skips loading
  const canCancel = isAdmin && !chargebee?.scheduled_cancellation_at

  return (
    <YourSubscription
      period={period ?? null}
      // TODO: STELLA-31 - with the translations, format with dayjs 'LL' (localizedFormat plugin) so dates follow each language's order
      renewsOn={chargebee?.expires_at ? formatDate(chargebee.expires_at) : null}
      expiresOn={
        chargebee?.scheduled_cancellation_at
          ? formatDate(chargebee.scheduled_cancellation_at)
          : null
      }
      amount={
        chargebee
          ? formatBillingAmount(
              chargebee.current_billing_amount_in_cents,
              chargebee.currency_code,
              locale,
            )
          : null
      }
      card={
        chargebee && !isBankDraftSubscriber ? getCardDetails(chargebee) : null
      }
      billedTo={chargebee ? getBilledToLines(chargebee, locale) : []}
      activeAddOns={activeAddOns}
      addOnsTotal={addOnsTotal}
      // TODO: STELLA-32/33 - replace the legacy add-on cards with the new offer cards
      addOnOffers={
        hasAddOnOffers && (
          <>
            {!hasDiscoverySuite && <DiscoverySuiteAddOns />}
            {!hasWatermarking && <WatermarkingAddOns />}
          </>
        )
      }
      manageSubscriptionUrl={pricingPage.url}
      isAdmin={isAdmin}
      manageSubscriptionDisabled={pricingPage.hasFailed}
      // TODO: STELLA-16 - pass onManageBilling once the Chargebee portal session endpoint exists
      cancelSubscription={
        canCancel && (
          <Flex align="center" gap="2">
            {needsChargebeeScript && (
              <ChargebeeScriptLoader
                onLoad={() => setHasChargebeeScriptLoaded(true)}
                onError={error => {
                  setHasChargebeeScriptLoaded(false)
                  captureError(error, {
                    description: 'ChargebeeScriptLoader in YourSubscription',
                  })
                }}
              />
            )}
            <SubscribedUserBillingCancelButton
              size="2"
              cancelSubscriptionModalOpen={cancelSubscriptionModalOpen}
              setCancelSubscriptionModalOpen={setCancelSubscriptionModalOpen}
              // The button waits for the script whenever the rollout is on, even when SUB-735 is off and nothing loads it
              hasChargebeeScriptLoaded={
                !needsChargebeeScript || hasChargebeeScriptLoaded
              }
            />
          </Flex>
        )
      }
      loadError={
        subscription.error && (
          <Callout.Root color="red">
            <Callout.Text>
              We couldn’t load your subscription details. Refresh the page to
              try again, or{' '}
              <ContactUsLink intercomMsg="My billing page couldn't load my subscription details.">
                contact support
              </ContactUsLink>
              .
            </Callout.Text>
          </Callout.Root>
        )
      }
    />
  )
}

export default YourSubscriptionContainer
